import {
	differenceInCalendarDays,
	endOfDay,
	isAfter,
	isWithinInterval,
	startOfDay,
} from 'date-fns'
import z from 'zod'

import {
	buildParamsSchema,
	findCatalogVariant,
	normalizeTacticParams,
} from '@/entities/pricing-rule/@x/pricing-campaign'

import { client } from '@/shared/api/client'
import { filtersSchema } from '@/shared/components/filter/validation'
import i18n from '@/shared/i18n/client'

import { PricingEngineType } from './types'

export const activitySchema = z
	.object({
		start_date: z.iso.date().optional().nullable(),
		end_date: z.iso.date().optional().nullable(),
		repeat_week_interval: z.number().optional().default(0),
		repeat_days: z
			.array(
				z.enum([
					'monday',
					'tuesday',
					'wednesday',
					'thursday',
					'friday',
					'saturday',
					'sunday',
				])
			)
			.optional(),
		inactive_strategy: z.string(),
		force_inactive: z.boolean().default(false),
	})
	.superRefine((data, ctx) => {
		if (
			data.start_date &&
			data.end_date &&
			isAfter(new Date(data.start_date), new Date(data.end_date))
		) {
			ctx.addIssue({
				code: 'custom',
				path: ['end_date'],
				message: 'End date cannot be before start date.',
			})
		}
	})

export const pricingTacticsPromoSchema = z.object({
	engine: z.literal(PricingEngineType.Promo),
	target_price_type: z.string().optional(),
	params: z
		.object({
			promo_start_date: z.string(),
			promo_end_date: z.string(),
			// Required by PromoPeriodModel. Must be declared: the resolver submits this
			// schema's parsed output, so an omitted key is stripped from the payload.
			target: z.object({ name: z.string(), value: z.number() }),
		})
		.superRefine((data, ctx) => {
			if (
				differenceInCalendarDays(
					new Date(data.promo_end_date),
					new Date(data.promo_start_date)
				) >= 14
			) {
				ctx.addIssue({
					code: 'custom',
					path: ['promo_start_date'],
					message: i18n.t('app.promo_period.error.max_range', {
						max: 14,
						defaultValue:
							'The promo period should be less or equal {max} days.',
					}),
				})
			}
		}),
})
export const pricingTacticsRBSchema = z.object({
	engine: z.literal(PricingEngineType.RB),
	target_price_type: z.string().optional(),
	params: z.object({
		pricing_rule_id: z.string(),
		pricing_rule_version_id: z.string(),
		rule_params: z.any().optional(),
	}),
})
export const pricingTacticsMLSchema = z.object({
	engine: z.literal(PricingEngineType.ML),
	target_price_type: z.string().optional(),
	params: z.any().optional(),
})
export const pricingTacticsMDSchema = z.object({
	engine: z.literal(PricingEngineType.MD),
	target_price_type: z.string().optional(),
	params: z.any(),
})
export const priceLimitRuleSchema = z.object({
	name: z.string(),
	value: z.number(),
	type: z.string(),
	on_violation: z.string(),
})
export const priceLimitsSchema = z.object({
	lower: z.array(priceLimitRuleSchema),
	upper: z.array(priceLimitRuleSchema),
	extra: z.object({
		on_conflict: z.string(),
	}),
})
export const anchoringRuleSchema = z.object({
	anchoring: z.string(),
	high_range: z.number(),
	high_range_behavior: z.string(),
	low_range: z.number(),
	low_range_behavior: z.string(),
})
export const anchoringSchema = z.object({
	rules: z.array(anchoringRuleSchema),
	on_conflict: z.string(),
	on_anchor_locked: z.string(),
	on_conflict_anchor_limits: z.string(),
	rounding_limits_and_price: z.string(),
})

export const minimalStepSchema = z.object({
	min_price: z.number(),
	min_step_absolute: z.number(),
	min_step_relative: z.number(),
})
export const productAssignmentSchema = z.object({
	name: z.string(),
	filters: filtersSchema,
})

export const pricingCampaignSettingsSchema = z.object({
	product_assignments: z.array(productAssignmentSchema),
	activity: activitySchema,
	price_limits: priceLimitsSchema,
	anchoring: anchoringSchema,
	rounding: z.array(z.any()),
	minimal_steps: z.array(minimalStepSchema),
	pricing_tactics: z.union([
		pricingTacticsPromoSchema,
		pricingTacticsRBSchema,
		pricingTacticsMLSchema,
		pricingTacticsMDSchema,
	]),
})
export const pricingTacticsRBSchemaAsync = z.object({
	engine: z.literal(PricingEngineType.RB),
	target_price_type: z.string().optional(),
	params: z
		.any()
		.superRefine(async (data, ctx) => {
			const pricingRulesCatalog = await client.pricingRule.pricingRuleCatalog()
			// Tolerates either stored shape: system rules keep `rule_id`, user rules
			// carry `pricing_rule_id` + `pricing_rule_version_id`.
			const pricingRule = findCatalogVariant(
				pricingRulesCatalog.data,
				normalizeTacticParams(data)
			)

			if (!pricingRule) {
				ctx.addIssue({
					code: 'custom',
					path: ['pricing_rule_id'],
					message: 'Pricing rule not found',
				})

				// Without this the params lookup below dereferences `undefined` and
				// throws inside an async refinement, which rejects the whole parse
				// instead of reporting an issue — crashing the caller.
				return
			}
			const suSchema = buildParamsSchema(pricingRule.params)
			const ruleParamsError = await suSchema.safeParseAsync(data.rule_params)
			if (ruleParamsError.error) {
				for (const issue of ruleParamsError.error.issues) {
					ctx.addIssue({
						...issue,
						path: ['rule_params', ...(issue.path as string[])],
					})
				}
			} else {
				data.rule_params = ruleParamsError.data
			}
		})
		// `part:tactics_rb` is a oneOf of mutually exclusive, `additionalProperties:
		// false` branches: a system rule must be `{ rule_id, rule_params }`, a user
		// rule `{ pricing_rule_id, pricing_rule_version_id, rule_params }`. Emitting
		// the wrong branch — or leaking both id keys — is a 422.
		.transform((data) => {
			const params = normalizeTacticParams(data)

			if (isSystemRule(params))
				return {
					rule_id: params.pricing_rule_id,
					rule_params: params.rule_params,
				}

			return {
				pricing_rule_id: params.pricing_rule_id,
				pricing_rule_version_id: params.pricing_rule_version_id,
				rule_params: params.rule_params,
			}
		}),
})
export const cretePricingCampaignSchemaAsync = z
	.object({
		settings: z.object({
			product_assignments: z.array(productAssignmentSchema),
			activity: activitySchema,
			price_limits: priceLimitsSchema,
			anchoring: anchoringSchema,
			rounding: z.array(z.any()),
			minimal_steps: z.array(minimalStepSchema),
			pricing_tactics: z.discriminatedUnion('engine', [
				pricingTacticsPromoSchema,
				pricingTacticsRBSchemaAsync,
				pricingTacticsMLSchema,
				pricingTacticsMDSchema,
			]),
		}),
	})
	.superRefine(async (data, ctx) => {
		if (data.settings.pricing_tactics.engine === PricingEngineType.Promo) {
			const tacticsStart = data.settings.pricing_tactics.params.promo_start_date
			const tacticsEnd = data.settings.pricing_tactics.params.promo_end_date
			const start_date = data.settings.activity.start_date
			const end_date = data.settings.activity.end_date
			if (start_date && end_date) {
				if (
					!isWithinInterval(startOfDay(new Date(tacticsStart)), {
						start: startOfDay(new Date(start_date)),
						end: endOfDay(new Date(end_date)),
					})
				) {
					ctx.addIssue({
						code: 'custom',
						path: ['settings', 'pricing_tactics', 'params', 'promo_start_date'],
						message:
							'Promo start date cannot be before a pricing campaign period start date or an integration date.',
					})
				}
				if (
					!isWithinInterval(endOfDay(new Date(tacticsEnd)), {
						start: startOfDay(new Date(start_date)),
						end: endOfDay(new Date(end_date)),
					})
				) {
					ctx.addIssue({
						code: 'custom',
						path: ['settings', 'pricing_tactics', 'params', 'promo_end_date'],
						message:
							'Promo end date cannot be later than a pricing campaign period end date.',
					})
				}
			}
		}
	})
/** System rules have no versions, so a missing version id identifies one. */
const isSystemRule = ({
	pricing_rule_version_id,
}: {
	pricing_rule_version_id: string | null
}) => !pricing_rule_version_id
export const pricingCampaignSchema = z.object({
	settings: pricingCampaignSettingsSchema,
})
